import { hash, type RecordOf, Set } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import type { AsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { authLogout, authSelectDataset } from '../auth/authActions';
import { type resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import { coreActions } from '../core-actions';
import * as actions from './institutionLoginsActions';
import {
  type InstitutionLoginProps,
  type InstitutionLoginStore,
  mkInstitutionLoginsStore,
} from './institutionLoginsTypes';

export const institutionLoginsReducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<InstitutionLoginProps, InstitutionLoginStore>,
  | ReturnType<typeof actions.dismissFiMigrationBanner>
  | ReturnType<typeof authLogout>
  | ReturnType<typeof authSelectDataset>
  | coreActions.SanitizeAction
  | coreActions.PersistAction
  | ReturnType<typeof actions.batchInstitutionLogins>
  | ReturnType<typeof actions.batchInstitutionLoginsSuccess>
  | ReturnType<typeof actions.getInstitutionLoginsSuccess>
  | ReturnType<typeof actions.updateInstitutionLogin>
  | ReturnType<typeof actions.updateInstitutionLoginSuccess>
  | ReturnType<typeof actions.deleteInstitutionLoginSuccess>
  | ReturnType<typeof actions.getInstitutionLoginsFailure>
  | ReturnType<typeof actions.credentialsFormSubmitSuccess>
  | ReturnType<typeof actions.refreshAccounts>
  | ReturnType<typeof actions.refreshAccountsStatuses>
  | ReturnType<typeof actions.refreshAccountsTriggeredMfa>
  | ReturnType<typeof actions.refreshAccountsCredentialsProvided>
  | ReturnType<typeof actions.refreshAccountsMfaFormSubmitResponse>
  | ReturnType<typeof actions.refreshAccountsCompleted>
  | ReturnType<typeof actions.addInstitutionLoginAndAccountsResponse>
  | ReturnType<typeof actions.institutionLoginSetCredentialsBlob>
  | ReturnType<typeof actions.qcsInstitutionLoginActionResponse>
  | ReturnType<typeof actions.updateChangedInstitutionLogins>
  | ReturnType<typeof actions.addDismissedFiIssue>
  | ReturnType<typeof actions.addDismissedAggStatus>
  | ReturnType<typeof actions.getLoginsWithFiIssuesSuccess>
  | ReturnType<typeof actions.updateRefreshStatus>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource: RecordOf<InstitutionLoginProps>) =>
          resource.merge({
            name: hash(resource.name).toString(),
          }),
        ),
      ),

    [coreActions.PERSIST_DATA_LOAD_COMPLETE]: (state) =>
      state.merge({
        isLoading: false,
        fiMigrationBannerDismissed: false,
        isRefreshingAll: false,
        idsRefreshing: Set(),
        idsRefreshingWithUserActionsPending: Set(),
        idsRefreshingWithCancelledUserActions: Set(),
      }),

    [actions.batchInstitutionLogins.type]: (state, action) => resourceUpserters.upsertResources(state, action.payload),
    [actions.batchInstitutionLoginsSuccess.type]: (state, action) =>
      resourceUpserters.upsertResources(state, action.payload),

    [actions.getInstitutionLoginsSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResources(state, payload),
    [actions.getInstitutionLoginsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    [actions.getLoginsWithFiIssuesSuccess.type]: (state, { payload }) =>
      state.set('resourcesById', state.resourcesById.merge(payload)),

    [actions.updateInstitutionLogin.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.updateInstitutionLogin),
    [actions.updateInstitutionLoginSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResource(state, payload),
    [actions.deleteInstitutionLoginSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResource(state, payload),

    [combineActions(
      actions.updateInstitutionLoginFailure,
      actions.deleteInstitutionLoginFailure,
      actions.batchInstitutionLoginsFailure,
    ).toString()]: (
      state: resourceStoreTypes.QcsSyncResourceStore<InstitutionLoginProps, InstitutionLoginStore>,
      { payload: error, asyncDispatch }: { payload: unknown; asyncDispatch: AsyncDispatch },
    ) => {
      asyncDispatch(actions.getInstitutionLogins());
      return resourceUpserters.resetResources(state, error);
    },

    [actions.credentialsFormSubmitSuccess.type]: (state, { payload: { institutionLogin } }) => {
      if (!state.resourcesById.get(institutionLogin?.id)) {
        // if this was a new institutionLogin (not an update), add to store
        return state.merge({ resourcesById: state.resourcesById.set(institutionLogin.id, institutionLogin) });
      }
      return state;
    },

    [actions.refreshAccounts.type]: (state, { payload: data }) => {
      if (data && data.institutionLoginId) {
        return state.set('idsRefreshing', state.idsRefreshing.add(data.institutionLoginId));
      }
      return state.set('isRefreshingAll', true);
    },

    [actions.refreshAccountsStatuses.type]: (state, { payload }) => {
      let { credentialBlobsForRefreshingLogins, credentialsForRefreshingLogins } = state;
      payload.institutionLogins.forEach((login) => {
        if (login.credentialsBlob && login.id) {
          credentialBlobsForRefreshingLogins = credentialBlobsForRefreshingLogins.set(login.id, login.credentialsBlob);
          credentialsForRefreshingLogins = credentialsForRefreshingLogins.delete(login.id);
        }
      });

      return state.merge({ credentialBlobsForRefreshingLogins, credentialsForRefreshingLogins });
    },

    [actions.refreshAccountsTriggeredMfa.type]: (state, { payload: id }) =>
      state.set('idsRefreshingWithUserActionsPending', state.idsRefreshingWithUserActionsPending.add(id)),

    [actions.refreshAccountsCredentialsProvided.type]: (state, { payload: { institutionLogin } }) => {
      let { credentialsForRefreshingLogins } = state;
      if (institutionLogin?.credentials != null && institutionLogin?.id) {
        credentialsForRefreshingLogins = credentialsForRefreshingLogins.set(
          institutionLogin.id,
          institutionLogin.credentials,
        );
      }
      return state.merge({
        idsRefreshingWithUserActionsPending: institutionLogin?.id
          ? state.idsRefreshingWithUserActionsPending.delete(institutionLogin.id)
          : state.idsRefreshingWithUserActionsPending,
        credentialsForRefreshingLogins,
      });
    },

    [combineActions(actions.refreshAccountsCredentialsNotProvided, actions.refreshAccountsMfaNotProvided).toString()]: (
      state: resourceStoreTypes.QcsSyncResourceStore<InstitutionLoginProps, InstitutionLoginStore>,
      { payload: id }: { payload: string },
    ) =>
      state.merge({
        idsRefreshingWithCancelledUserActions: state.idsRefreshingWithCancelledUserActions.add(id),
        idsRefreshingWithUserActionsPending: state.idsRefreshingWithUserActionsPending.delete(id),
      }),

    [actions.refreshAccountsMfaFormSubmitResponse.type]: (state, { payload }) => {
      if (payload.error) {
        return state.merge({
          idsRefreshingWithCancelledUserActions: state.idsRefreshingWithCancelledUserActions.add(
            payload.institutionLoginId,
          ),
          idsRefreshingWithUserActionsPending: state.idsRefreshingWithUserActionsPending.delete(
            payload.institutionLoginId,
          ),
        });
      }
      return state.set(
        'idsRefreshingWithUserActionsPending',
        state.idsRefreshingWithUserActionsPending.delete(payload.institutionLoginId),
      );
    },

    [actions.refreshAccountsCompleted.type]: (state, { payload }) => {
      const { institutionLoginId } = payload;
      if (institutionLoginId) {
        return state.merge({
          idsRefreshing: state.idsRefreshing.remove(institutionLoginId),
          idsRefreshingWithUserActionsPending: state.idsRefreshingWithUserActionsPending.remove(institutionLoginId),
          idsRefreshingWithCancelledUserActions: state.idsRefreshingWithCancelledUserActions.remove(institutionLoginId),

          credentialsForRefreshingLogins: state.credentialsForRefreshingLogins.remove(institutionLoginId),
        });
      }
      return state.merge({
        isRefreshingAll: false,

        idsRefreshing: state.idsRefreshing.clear(),
        idsRefreshingWithUserActionsPending: state.idsRefreshingWithUserActionsPending.clear(),
        idsRefreshingWithCancelledUserActions: state.idsRefreshingWithCancelledUserActions.clear(),

        credentialsForRefreshingLogins: state.credentialsForRefreshingLogins.clear(),
      });
    },

    [actions.addInstitutionLoginAndAccountsResponse.type]: (state, { payload: { institutionLogin } }) => {
      if (!institutionLogin) {
        return state;
      }
      let newState = resourceUpserters.upsertResource<InstitutionLoginProps, InstitutionLoginStore>(
        state,
        institutionLogin,
      );
      // Exclude zillow and Apple from Refresh/idsRefreshing.
      if (institutionLogin?.channel !== 'APPLE_WALLET' && institutionLogin?.channel !== 'ZILLOW') {
        newState = newState.set('idsRefreshing', state.idsRefreshing.add(institutionLogin?.id));
      }
      return newState;
    },

    [actions.institutionLoginSetCredentialsBlob.type]: (state, { payload }) => {
      const { institutionLoginId, credentialsBlob } = payload;

      let { credentialBlobsForRefreshingLogins, credentialsForRefreshingLogins } = state;
      credentialBlobsForRefreshingLogins = credentialBlobsForRefreshingLogins.set(institutionLoginId, credentialsBlob);
      credentialsForRefreshingLogins = credentialsForRefreshingLogins.delete(institutionLoginId);

      return state.merge({ credentialBlobsForRefreshingLogins, credentialsForRefreshingLogins });
    },

    [actions.qcsInstitutionLoginActionResponse.type]: (state, { payload }) => {
      return resourceUpserters.upsertResource(state, payload, false);
    },

    [actions.updateChangedInstitutionLogins.type]: (state, { payload: institutionLogins }) =>
      resourceUpserters.upsertResources(state, { resources: institutionLogins, doMerge: false }),
    [actions.dismissFiMigrationBanner.type]: (state) => state.set('fiMigrationBannerDismissed', true),
    [authLogout.type]: (state) => state.set('fiMigrationBannerDismissed', false),
    [authSelectDataset.type]: (state) => state.set('fiMigrationBannerDismissed', false),
    [actions.addDismissedFiIssue.type]: (state, { payload }) => {
      const { id, type } = payload;
      return state.set('dismissedFiIssuesByLoginId', state.dismissedFiIssuesByLoginId.set(id, type));
    },
    [actions.addDismissedAggStatus.type]: (state, { payload }) => {
      const { id, aggStatus } = payload;
      return state.set('dismissedAggStatusesByLoginId', state.dismissedAggStatusesByLoginId.set(id, aggStatus));
    },
    [actions.updateRefreshStatus.type]: (state, { payload: institutionLogin }) =>
      resourceUpserters.upsertResource<InstitutionLoginProps, InstitutionLoginStore>(state, institutionLogin),
  },
  mkInstitutionLoginsStore(),
);
